// ══════════════════════════════════════════════════════════════
// Login.js — Autenticación CliniFlow
// Flujos: login · olvidé contraseña · nueva contraseña
// ══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";

const C = {
  bg:      "#0B0F19",
  card:    "#141926",
  border:  "rgba(255,255,255,0.08)",
  lime:    "#2DD4C8",
  limeDim: "rgba(45,212,200,0.10)",
  limeBorder: "rgba(45,212,200,0.25)",
  text:    "#F1F5F9",
  muted:   "#94A3B8",
  dim:     "#475569",
  surface: "#1E2535",
  red:     "#f87171", redBg: "#1a0808", redBorder: "rgba(248,113,113,0.25)",
  green:   "#4ade80", greenBg: "#0a1a0f", greenBorder: "rgba(74,222,128,0.25)",
};

const inputBase = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 8,
  border: `1px solid ${C.border}`,
  background: C.surface,
  color: C.text,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  transition: "border-color 0.15s",
};

const Label = ({ children }) => (
  <label style={{
    fontSize: 11, color: C.muted, display: "block", marginBottom: 6,
    fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em",
  }}>
    {children}
  </label>
);

const Alert = ({ type, children }) => {
  const styles = type === "error"
    ? { bg: C.redBg,   border: C.redBorder,   color: C.red   }
    : { bg: C.greenBg, border: C.greenBorder, color: C.green };
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 8,
      background: styles.bg, border: `1px solid ${styles.border}`,
      fontSize: 13, color: styles.color, marginBottom: 16,
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
};

const Btn = ({ children, disabled, cargando, secondary, onClick, type = "submit", style = {} }) => (
  <button
    type={type}
    disabled={disabled || cargando}
    onClick={onClick}
    style={{
      width: "100%",
      padding: "clamp(11px, 3vw, 14px)",
      borderRadius: 8,
      border: secondary ? `1px solid ${C.border}` : "none",
      background: secondary ? "transparent" : C.lime,
      color: secondary ? C.muted : "#fff",
      fontSize: "clamp(13px, 3.5vw, 14px)",
      fontWeight: 700,
      cursor: (disabled || cargando) ? "wait" : "pointer",
      opacity: (disabled || cargando) ? 0.6 : 1,
      fontFamily: "inherit",
      transition: "opacity 0.15s, background 0.15s",
      minHeight: 44,
      ...style,
    }}
  >
    {cargando ? "Un momento..." : children}
  </button>
);

const Logo = ({ clinica }) => (
  <div style={{ textAlign: "center", marginBottom: "clamp(24px, 6vw, 40px)" }}>
    {clinica?.logo_url ? (
      <img src={clinica.logo_url} alt={clinica.nombre} style={{
        width: "clamp(64px, 16vw, 80px)", height: "clamp(64px, 16vw, 80px)",
        borderRadius: 16, objectFit: "contain", marginBottom: 14,
      }} />
    ) : (
      <div style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: "clamp(40px, 10vw, 48px)", height: "clamp(40px, 10vw, 48px)", borderRadius: 12,
        background: C.lime, marginBottom: 14,
        fontSize: "clamp(20px, 5vw, 24px)", fontWeight: 900, color: C.bg,
      }}>C</div>
    )}
    <div style={{ fontSize: "clamp(22px, 6vw, 26px)", fontWeight: 800, color: C.text, letterSpacing: "-0.5px" }}>
      {clinica ? clinica.nombre : <><span style={{ fontWeight: 400 }}>Clini</span><span style={{ color: C.lime }}>Flow</span></>}
    </div>
    <div style={{ fontSize: "clamp(12px, 3.5vw, 13px)", color: C.muted, marginTop: 6 }}>
      {clinica ? "Acceso para el equipo" : "Gestión inteligente para tu clínica"}
    </div>
  </div>
);

const Card = ({ children }) => (
  <div style={{
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "clamp(20px, 6vw, 32px) clamp(16px, 5vw, 28px)",
  }}>
    {children}
  </div>
);

// ─── VISTA: LOGIN ────────────────────────────────────────────
function ViewLogin({ onLogin, onForgot }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError]       = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Rellena email y contraseña"); return; }
    setCargando(true);
    setError(null);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (authError) throw authError;
      if (!data.session) throw new Error("No se pudo iniciar sesión");
      onLogin(data.session);
    } catch (err) {
      const msg = err.message;
      if (msg === "Invalid login credentials") setError("Email o contraseña incorrectos. Revísalos e inténtalo de nuevo.");
      else if (msg === "Email not confirmed") setError("Confirma tu email antes de acceder. Revisa tu bandeja de entrada.");
      else if (msg.includes("Too many requests")) setError("Demasiados intentos. Espera unos minutos antes de volver a intentarlo.");
      else setError(msg);
    } finally { setCargando(false); }
  };

  return (
    <Card>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 24 }}>
        Iniciar sesión
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <Label>Email</Label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="tu@email.com"
            autoFocus
            autoComplete="email"
            style={inputBase}
            onFocus={e => e.target.style.borderColor = C.lime}
            onBlur={e => e.target.style.borderColor = C.border}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <Label>Contraseña</Label>
            <button
              type="button"
              onClick={() => onForgot(email)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, color: C.muted, padding: 0,
                textDecoration: "underline", textUnderlineOffset: 2,
              }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          <div style={{ position: "relative" }}>
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              style={{ ...inputBase, paddingRight: 44 }}
              onFocus={e => e.target.style.borderColor = C.lime}
              onBlur={e => e.target.style.borderColor = C.border}
            />
            <button
              type="button"
              onClick={() => setShowPwd(v => !v)}
              tabIndex={-1}
              style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer",
                color: C.dim, fontSize: 16, padding: "4px",
              }}
              title={showPwd ? "Ocultar contraseña" : "Ver contraseña"}
            >
              {showPwd ? "🙈" : "👁"}
            </button>
          </div>
        </div>

        <Btn cargando={cargando}>
          {cargando ? "Entrando..." : "Entrar →"}
        </Btn>
      </form>
    </Card>
  );
}

// ─── VISTA: OLVIDÉ MI CONTRASEÑA ────────────────────────────
function ViewForgot({ initialEmail = "", onBack }) {
  const [email, setEmail]     = useState(initialEmail);
  const [cargando, setCargando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Introduce tu email"); return; }
    setCargando(true);
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: window.location.origin + "/?reset=1" }
      );
      if (resetError) throw resetError;
      setEnviado(true);
    } catch (err) {
      setError(err.message || "Error al enviar el email. Inténtalo de nuevo.");
    } finally { setCargando(false); }
  };

  return (
    <Card>
      <div style={{ marginBottom: 20 }}>
        <button
          type="button"
          onClick={onBack}
          style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 13, padding: 0, display: "flex", alignItems: "center", gap: 6 }}
        >
          ← Volver
        </button>
      </div>

      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
        Recuperar contraseña
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.5 }}>
        Escribe tu email y te enviaremos un enlace para crear una contraseña nueva.
      </div>

      {error   && <Alert type="error">{error}</Alert>}
      {enviado && (
        <Alert type="success">
          ✓ Email enviado. Revisa tu bandeja de entrada (y la carpeta de spam).
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
            El enlace caduca en 1 hora.
          </div>
        </Alert>
      )}

      {!enviado ? (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <Label>Tu email</Label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="tu@email.com"
              autoFocus
              style={inputBase}
              onFocus={e => e.target.style.borderColor = C.lime}
              onBlur={e => e.target.style.borderColor = C.border}
            />
          </div>
          <Btn cargando={cargando}>Enviar enlace</Btn>
        </form>
      ) : (
        <Btn secondary onClick={onBack} type="button">← Volver al inicio de sesión</Btn>
      )}
    </Card>
  );
}

// ─── VISTA: NUEVA CONTRASEÑA (tras clic en email de reset) ──
function ViewReset({ onBack }) {
  const [password, setPassword]     = useState("");
  const [confirm, setConfirm]       = useState("");
  const [showPwd, setShowPwd]       = useState(false);
  const [cargando, setCargando]     = useState(false);
  const [hecho, setHecho]           = useState(false);
  const [error, setError]           = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8)     { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    if (password !== confirm)    { setError("Las contraseñas no coinciden"); return; }
    setCargando(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setHecho(true);
    } catch (err) {
      setError(err.message || "Error al actualizar la contraseña.");
    } finally { setCargando(false); }
  };

  return (
    <Card>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
        Nueva contraseña
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
        Elige una contraseña segura para tu cuenta.
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {hecho ? (
        <>
          <Alert type="success">
            ✓ Contraseña actualizada correctamente. Ya puedes entrar con tu nueva contraseña.
          </Alert>
          <Btn onClick={onBack} type="button">Ir al inicio de sesión</Btn>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <Label>Nueva contraseña</Label>
            <div style={{ position: "relative" }}>
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                autoFocus
                style={{ ...inputBase, paddingRight: 44 }}
                onFocus={e => e.target.style.borderColor = C.lime}
                onBlur={e => e.target.style.borderColor = C.border}
              />
              <button type="button" onClick={() => setShowPwd(v => !v)} tabIndex={-1}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.dim, fontSize: 16, padding: "4px" }}>
                {showPwd ? "🙈" : "👁"}
              </button>
            </div>
            {password && (
              <PasswordStrength password={password} />
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <Label>Confirmar contraseña</Label>
            <input
              type={showPwd ? "text" : "password"}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repite la contraseña"
              style={{
                ...inputBase,
                borderColor: confirm && confirm !== password ? C.red : C.border,
              }}
              onFocus={e => e.target.style.borderColor = confirm !== password ? C.red : C.lime}
              onBlur={e => e.target.style.borderColor = confirm && confirm !== password ? C.red : C.border}
            />
            {confirm && confirm !== password && (
              <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>Las contraseñas no coinciden</div>
            )}
          </div>

          <Btn cargando={cargando} disabled={!password || !confirm}>
            Guardar contraseña
          </Btn>
        </form>
      )}
    </Card>
  );
}

// ─── INDICADOR DE FORTALEZA ──────────────────────────────────
function PasswordStrength({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ["Muy débil", "Débil", "Aceptable", "Fuerte", "Muy fuerte"];
  const colors = ["#f87171", "#fb923c", "#facc15", "#4ade80", "#4ade80"];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i < score ? colors[score] : C.border,
            transition: "background 0.2s",
          }} />
        ))}
      </div>
      <div style={{ fontSize: 11, color: colors[score] }}>{labels[score]}</div>
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────
export default function Login({ onLogin, clinica }) {
  // Si hay clínica, sobreescribir colores globales
  const [vista, setVista]         = useState("login");
  const [emailParaReset, setEmailParaReset] = useState("");

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    if (hash.includes("type=recovery") || params.get("reset") === "1") setVista("reset");
  }, []);

  const handleForgot = (email = "") => { setEmailParaReset(email); setVista("forgot"); };

  // Colores: si hay clínica usa los suyos, si no los de CliniFlow
  const bg      = clinica ? (clinica.color_fondo || "#0A0A0A") : C.bg;
  const card    = clinica ? (clinica.color_sidebar || "#111111") : C.card;
  const accent  = clinica ? (clinica.color_primario || "#C6FF00") : C.lime;
  const border  = clinica ? "#222222" : C.border;
  const surface = clinica ? "#161616" : C.surface;
  const muted   = clinica ? "#888888" : C.muted;
  const dim     = clinica ? "#444444" : C.dim;

  return (
    <div style={{
      minHeight: "100vh", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "var(--font-sans, 'Inter', system-ui, sans-serif)",
      padding: "clamp(16px, 5vw, 32px)",
    }}>
      <div style={{ width: "100%", maxWidth: "min(400px, calc(100vw - 32px))" }}>
        {clinica && (
          <div style={{ textAlign: "center", marginBottom: 8, fontSize: 12, color: muted }}>
            Powered by CliniFlow
          </div>
        )}
        <Logo clinica={clinica} />

        {/* Card de login con colores de la clínica */}
        <div style={{
          background: card, border: `1px solid ${border}`, borderRadius: 16,
          padding: "clamp(20px, 6vw, 32px) clamp(16px, 5vw, 28px)",
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 24 }}>
            Iniciar sesión
          </div>

          {vista === "login" && (
            <ViewLoginInline onLogin={onLogin} onForgot={handleForgot}
              accent={accent} border={border} surface={surface} />
          )}
          {vista === "forgot" && <ViewForgot initialEmail={emailParaReset} onBack={() => setVista("login")} />}
          {vista === "reset"  && <ViewReset  onBack={() => { setVista("login"); window.history.replaceState({}, "", "/"); }} />}
        </div>

        <div style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: dim }}>
          {clinica ? `${clinica.nombre} · Powered by CliniFlow` : "CliniFlow · Gestión de clínicas de fisioterapia"}
        </div>
      </div>
    </div>
  );
}

// Login inline que acepta colores custom
function ViewLoginInline({ onLogin, onForgot, accent, border, surface }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError]       = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Rellena email y contraseña"); return; }
    setCargando(true); setError(null);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
      if (authError) throw authError;
      if (!data.session) throw new Error("No se pudo iniciar sesión");
      onLogin(data.session);
    } catch (err) {
      const msg = err.message;
      if (msg === "Invalid login credentials") setError("Email o contraseña incorrectos.");
      else if (msg.includes("Too many requests")) setError("Demasiados intentos. Espera unos minutos.");
      else setError(msg);
    } finally { setCargando(false); }
  };

  const iStyle = { ...inputBase, border: `1px solid ${border}`, background: surface, color: "#F0F0F0" };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#1a0808", border: "1px solid rgba(248,113,113,0.25)", fontSize: 13, color: "#f87171", marginBottom: 16 }}>{error}</div>}
      <div style={{ marginBottom: 16 }}>
        <Label>Email</Label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="tu@email.com" autoFocus autoComplete="email"
          style={iStyle} onFocus={e => e.target.style.borderColor = accent} onBlur={e => e.target.style.borderColor = border} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <Label>Contraseña</Label>
          <button type="button" onClick={() => onForgot(email)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#888", padding: 0, textDecoration: "underline" }}>
            ¿Olvidaste tu contraseña?
          </button>
        </div>
        <div style={{ position: "relative" }}>
          <input type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
            placeholder="••••••••" autoComplete="current-password"
            style={{ ...iStyle, paddingRight: 44 }} onFocus={e => e.target.style.borderColor = accent} onBlur={e => e.target.style.borderColor = border} />
          <button type="button" onClick={() => setShowPwd(v => !v)} tabIndex={-1}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#555", fontSize: 16, padding: "4px" }}>
            {showPwd ? "🙈" : "👁"}
          </button>
        </div>
      </div>
      <button type="submit" disabled={cargando}
        style={{ width: "100%", padding: 14, borderRadius: 8, border: "none", background: accent, color: "#000", fontSize: 14, fontWeight: 700, cursor: cargando ? "wait" : "pointer", opacity: cargando ? 0.6 : 1, minHeight: 44 }}>
        {cargando ? "Entrando..." : "Entrar →"}
      </button>
    </form>
  );
}
